// Translation keys for next-intl integration
// These map to the message keys in the translation JSON files

// Game Status Translation Keys
export const GAME_STATUS_KEYS = {
  BACKLOG: 'gameStatus.backlog',
  WISHLIST: 'gameStatus.wishlist',
  COMPLETED: 'gameStatus.completed',
  PLAYING: 'gameStatus.playing',
} as const;

// Action Button Translation Keys
export const ACTION_KEYS = {
  SAVE: 'actions.save',
  CANCEL: 'actions.cancel',
  DELETE: 'actions.delete',
  EDIT: 'actions.edit',
  CREATE: 'actions.create',
  ADD: 'actions.add',
  REMOVE: 'actions.remove',
  UPDATE: 'actions.update',
  SUBMIT: 'actions.submit',
  CONFIRM: 'actions.confirm',
} as const;

// Loading State Translation Keys
export const LOADING_KEYS = {
  LOADING: 'loading.loading',
  SAVING: 'loading.saving',
  ADDING: 'loading.adding',
  UPDATING: 'loading.updating',
  DELETING: 'loading.deleting',
  CREATING: 'loading.creating',
  REMOVING: 'loading.removing',
} as const;

// Collection Translation Keys
export const COLLECTION_KEYS = {
  ADD_TO_COLLECTION: 'collection.addToCollection',
  REMOVE_FROM_COLLECTION: 'collection.removeFromCollection',
  UPDATE_COLLECTION: 'collection.updateCollection',
  LOADING_COLLECTION: 'collection.loadingCollection',
  FAILED_TO_LOAD_COLLECTION: 'collection.failedToLoadCollection',
  FAILED_TO_ADD_TO_COLLECTION: 'collection.failedToAddToCollection',
  FAILED_TO_UPDATE_STATUS: 'collection.failedToUpdateStatus',
  FAILED_TO_REMOVE_FROM_COLLECTION: 'collection.failedToRemoveFromCollection',
  MY_GAME_COLLECTION: 'collection.myGameCollection',
  ALL_GAMES: 'collection.allGames',
  NO_GAMES_FOUND: 'collection.noGamesFound',
  START_BUILDING: 'collection.startBuilding',
  NO_GAMES_WITH_STATUS: 'collection.noGamesWithStatus',
  GAME_COUNT: 'collection.gameCount',
  GAME_COUNT_SIMPLE: 'collection.gameCountSimple',
  CHANGE_GAME_STATUS: 'collection.changeGameStatus',
  REMOVE_FROM_COLLECTION_TOOLTIP: 'collection.removeFromCollectionTooltip',
  ADDED_ON: 'collection.addedOn',
  PLATFORM: 'collection.platform',
  DEVELOPER: 'collection.developer',
  LOAD_MORE_GAMES: 'collection.loadMoreGames',
  CONFIRM_REMOVE_TITLE: 'collection.confirmRemoveTitle',
  CONFIRM_REMOVE_MESSAGE: 'collection.confirmRemoveMessage',
  CONFIRM_REMOVE_GAME_NAME: 'collection.confirmRemoveGameName',
  TYPE_TO_CONFIRM: 'collection.typeToConfirm',
  TYPE_TO_CONFIRM_PLACEHOLDER: 'collection.typeToConfirmPlaceholder',
} as const;

// Journal Translation Keys
export const JOURNAL_KEYS = {
  CREATE_ENTRY: 'journal.createEntry',
  SAVE_ENTRY: 'journal.saveEntry',
  EDIT_ENTRY: 'journal.editEntry',
  DELETE_ENTRY: 'journal.deleteEntry',
  CANCEL_EDIT: 'journal.cancelEdit',
  LOADING_ENTRIES: 'journal.loadingEntries',
  NO_ENTRIES: 'journal.noEntries',
  FAILED_TO_LOAD_ENTRIES: 'journal.failedToLoadEntries',
  FAILED_TO_SAVE_ENTRY: 'journal.failedToSaveEntry',
  FAILED_TO_DELETE_ENTRY: 'journal.failedToDeleteEntry',
  ENTRY_TITLE: 'journal.entryTitle',
  CONTENT: 'journal.content',
  CHOOSE_GAME: 'journal.chooseGame',
  GAME: 'journal.game',
  CONTENT_PLACEHOLDER: 'journal.contentPlaceholder',
} as const;

// Rich text editor Translation Keys
export const EDITOR_KEYS = {
  PLACEHOLDER: 'editor.placeholder',
  TOOLBAR_LABEL: 'editor.toolbarLabel',
  BLOCK_TYPE: 'editor.blockType',
  PARAGRAPH: 'editor.paragraph',
  HEADING_1: 'editor.heading1',
  HEADING_2: 'editor.heading2',
  HEADING_3: 'editor.heading3',
  HEADING_4: 'editor.heading4',
  HEADING_5: 'editor.heading5',
  HEADING_6: 'editor.heading6',
  QUOTE: 'editor.quote',
  CODE_BLOCK: 'editor.codeBlock',
  BOLD: 'editor.bold',
  ITALIC: 'editor.italic',
  UNDERLINE: 'editor.underline',
  STRIKETHROUGH: 'editor.strikethrough',
  INLINE_CODE: 'editor.inlineCode',
  LINK: 'editor.link',
  EDIT_LINK: 'editor.editLink',
  REMOVE_LINK: 'editor.removeLink',
  OPEN_LINK: 'editor.openLink',
  LINK_URL: 'editor.linkUrl',
  LINK_PLACEHOLDER: 'editor.linkPlaceholder',
  APPLY_LINK: 'editor.applyLink',
  INVALID_LINK: 'editor.invalidLink',
  BULLET_LIST: 'editor.bulletList',
  ORDERED_LIST: 'editor.orderedList',
  TASK_LIST: 'editor.taskList',
  INSERT_TABLE: 'editor.insertTable',
  TABLE: 'editor.table',
  ADD_ROW_AFTER: 'editor.addRowAfter',
  ADD_COLUMN_AFTER: 'editor.addColumnAfter',
  DELETE_ROW: 'editor.deleteRow',
  DELETE_COLUMN: 'editor.deleteColumn',
  TOGGLE_HEADER_ROW: 'editor.toggleHeaderRow',
  DELETE_TABLE: 'editor.deleteTable',
  HORIZONTAL_RULE: 'editor.horizontalRule',
  UNDO: 'editor.undo',
  REDO: 'editor.redo',
  SLASH_MENU_LABEL: 'editor.slashMenuLabel',
  SLASH_NO_RESULTS: 'editor.slashNoResults',
  TOO_LONG: 'editor.tooLong',
} as const;

// Todo Translation Keys
export const TODO_KEYS = {
  ADD_ITEM_PLACEHOLDER: 'todo.addItemPlaceholder',
  NEW_LIST_PLACEHOLDER: 'todo.newListPlaceholder',
  DEFAULT_LIST_TITLE: 'todo.defaultListTitle',
  EMPTY_TITLE: 'todo.emptyTitle',
  EMPTY_HINT: 'todo.emptyHint',
  DONE: 'todo.done',
  SHOW_DONE: 'todo.showDone',
  HIDE_DONE: 'todo.hideDone',
  NOTES_PLACEHOLDER: 'todo.notesPlaceholder',
  ADD_NOTE: 'todo.addNote',
  EDIT_ITEM: 'todo.editItem',
  DELETE_ITEM: 'todo.deleteItem',
  DRAG_TO_REORDER: 'todo.dragToReorder',
  MARK_COMPLETED: 'todo.markCompleted',
  MARK_INCOMPLETE: 'todo.markIncomplete',
  LIST_OPTIONS: 'todo.listOptions',
  RENAME_LIST: 'todo.renameList',
  CLEAR_COMPLETED: 'todo.clearCompleted',
  DELETE_LIST: 'todo.deleteList',
  CONFIRM_DELETE_LIST_TITLE: 'todo.confirmDeleteListTitle',
  CONFIRM_DELETE_LIST_MESSAGE: 'todo.confirmDeleteListMessage',
  ITEM_DELETED: 'todo.itemDeleted',
  ITEMS_CLEARED: 'todo.itemsCleared',
  UNDO: 'todo.undo',
  SAVE_FAILED: 'todo.saveFailed',
  PROGRESS: 'todo.progress',
  KEYBOARD_HINT: 'todo.keyboardHint',
  LOADING: 'todo.loading',
  LOAD_FAILED: 'todo.loadFailed',
} as const;

// Auth Translation Keys
export const AUTH_KEYS = {
  LOGIN: 'auth.login',
  SIGNUP: 'auth.signup',
  LOGOUT: 'auth.logout',
  SIGN_IN: 'auth.signIn',
  SIGN_UP: 'auth.signUp',
  SIGN_OUT: 'auth.signOut',
  LOGGING_IN: 'auth.loggingIn',
  SIGNING_UP: 'auth.signingUp',
  FAILED_TO_LOGIN: 'auth.failedToLogin',
  FAILED_TO_SIGNUP: 'auth.failedToSignup',
  APP_TITLE: 'auth.appTitle',
  SIGN_IN_WITH_STEAM: 'auth.signInWithSteam',
  OR: 'auth.or',
  EMAIL: 'auth.email',
  USERNAME: 'auth.username',
  PASSWORD: 'auth.password',
  CONFIRM_PASSWORD: 'auth.confirmPassword',
  ENTER_EMAIL: 'auth.enterEmail',
  ENTER_USERNAME: 'auth.enterUsername',
  ENTER_PASSWORD: 'auth.enterPassword',
  CONFIRM_PASSWORD_PLACEHOLDER: 'auth.confirmPasswordPlaceholder',
  LOGIN_FAILED: 'auth.loginFailed',
  CREATING_ACCOUNT: 'auth.creatingAccount',
  REGISTRATION_FAILED: 'auth.registrationFailed',
  PASSWORDS_DO_NOT_MATCH: 'auth.passwordsDoNotMatch',
  DONT_HAVE_ACCOUNT: 'auth.dontHaveAccount',
  ALREADY_HAVE_ACCOUNT: 'auth.alreadyHaveAccount',
  JOIN_GAMERS_NOTEBOOK: 'auth.joinGamersNotebook',
} as const;

// UI Translation Keys
export const UI_KEYS = {
  TRY_AGAIN: 'ui.tryAgain',
  LOAD_MORE: 'ui.loadMore',
  NO_RESULTS: 'ui.noResults',
  SEARCH_PLACEHOLDER: 'ui.searchPlaceholder',
  EMPTY_STATE: 'ui.emptyState',
  UNEXPECTED_ERROR: 'ui.unexpectedError',
} as const;

// Navigation Translation Keys
export const NAVIGATION_KEYS = {
  DASHBOARD: 'navigation.dashboard',
  COLLECTION: 'navigation.collection',
  JOURNAL: 'navigation.journal',
  GAMES: 'navigation.games',
  BELONGS_TO: 'navigation.belongsTo',
} as const;

// Game Details Translation Keys
export const GAME_DETAILS_KEYS = {
  LOADING_GAME_DETAILS: 'gameDetails.loadingGameDetails',
  GAME_NOT_FOUND: 'gameDetails.gameNotFound',
  GAME_NOT_FOUND_MESSAGE: 'gameDetails.gameNotFoundMessage',
  STATUS: 'gameDetails.status',
  CHANGE_GAME_STATUS: 'gameDetails.changeGameStatus',
  PLATFORM: 'gameDetails.platform',
  DEVELOPER: 'gameDetails.developer',
  RELEASED: 'gameDetails.released',
  TODOS: 'gameDetails.todos',
  JOURNAL: 'gameDetails.journal',
} as const;

// Game Status Values (unchanged)
export const GAME_STATUS = {
  BACKLOG: 'backlog',
  WISHLIST: 'wishlist',
  COMPLETED: 'completed',
  PLAYING: 'playing',
} as const;

export type GameStatus = (typeof GAME_STATUS)[keyof typeof GAME_STATUS];
